import React, { FC, useEffect } from "react";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { useActions } from "@hooks/useActions";
import LoadingBox from "../../../components/elements/loading/LoadingBox";
import Sticker from "../../../components/elements/sticker/Sticker";
import { ApiSelect, ApiStateSelect } from "@baseTypes/baseType";
import st from "./styles.module.scss";
import { AsyncThunk } from "@reduxjs/toolkit";
import { SelectorCropTNode } from "../../../store/reducers/dashboard/crops/selector/types";
import { ActionsType } from "../../../store/reducers/action-creators";
import { compact } from 'fp-ts/es6/Array'
import { array, option } from "fp-ts";
import { useParams, useSearchParams } from "react-router-dom";
import { getYear } from "date-fns";

export interface CropSelectorProps {
    crop?: SelectorCropTNode
}

export const StickerLoadingBox: FC<any> = () => {
    return <Sticker style={{ minHeight: '400px' }}><LoadingBox borderRadius={"10"}/></Sticker>
}
export const StickerErrorBox: FC<{ exception?: string, className?: string }> = ({ exception, className }) => {
    return <Sticker style={{ minHeight: '400px' }} className={className}><span className={st.error}>{exception}</span></Sticker>
}
export type LoadingProps = { isLoading: boolean, exception: string }
type LoadingOptions = {
    preventStickerBox?: boolean
    cropEnabled?: boolean
}

export const stickerLoading = <T extends LoadingProps>(Component: FC<T>) => (props: T) => {
    const { isLoading, exception } = props
    if (exception) {
        return <StickerErrorBox exception={exception}/>
    }
    if (isLoading) {
        return <StickerLoadingBox/>
    }
    return <Component {...props}></Component>
}

export type DashboardQueryParams = {
    cropRotationIds?: string[],
    year?: number,
    tzOffset?: number,
    date: number
}

export type StickerParams = Record<string, string>
export const stickerLoadingWithCrop = <T extends any, R>(selector: ApiSelect<T>,
                                                      // TODO! R should be more specific
                                                      fn: (a: ActionsType) =>
                                                          (dashParams: DashboardQueryParams, params: StickerParams) => R extends void ? never : R,
                                                      options: LoadingOptions = { preventStickerBox: false, cropEnabled: true }
) => (Component: FC<any>) => {
    const Underlying = options.preventStickerBox ? Component : stickerLoading(Component)
    return (props: any) => {
        let {
            selected: crop,
            isApiLoading: isCropApiLoading,
            apiError: cropApiError
        } = useTypedSelector(state => state.cropSelector)
        let { selectedDate } = useTypedSelector(state => state.selectedDate)
        let { isApiLoading, apiError } = useTypedSelector(selector)
        const actions: ActionsType = useActions();
        const params: StickerParams = useParams() as StickerParams;
        const [searchParams, _] = useSearchParams()
        useEffect(() => {
            if (array.isNonEmpty(crop || []) || options.cropEnabled === false) {
                const pickedDate = selectedDate || new Date()

                const res  = fn(actions)({
                    cropRotationIds: crop?.flatMap(i => i.cropRotationIds) || [],
                    year: getYear(pickedDate),
                    tzOffset: new Date().getTimezoneOffset(),
                    date: pickedDate.getTime()
                }, {...params, ...Object.fromEntries(searchParams) }) as { abort(): void };

                return () =>
                    // @ts-expect-error
                    res && [].concat(res).forEach((res) => res?.abort?.())
            }
        }, options.cropEnabled ? [crop] : [selectedDate]);
        const exception = cropApiError || apiError
        const isLoad = isApiLoading || isCropApiLoading || options.cropEnabled && !crop || !selectedDate

        return <Underlying
            exception={exception}
            isLoading={isLoad}
            crop={crop} {...props}></Underlying>
    }
}

export const applyLoading = (selector: ApiStateSelect, fn: (a: ActionsType) => AsyncThunk<any, void, {}>[]) => (Component: FC<any>) => {
    return (props: any) => {
        let apis = useTypedSelector(selector)
        let isApiLoading = apis.every(i => i.isApiLoading)
        let apiError = compact(apis.map(i => option.fromNullable(i.apiError)))
        const actions: ActionsType = useActions();
        useEffect(() => {
            fn(actions).forEach((action) => action())
        }, [])

        if (apiError.length > 0) {
            return <span className={st.error}>
                {apiError.join(',')}</span>
        }

        if (isApiLoading) {
            return <LoadingBox/>;
        }

        return <Component  {...props}></Component>
    }
}

// todo: move component in different folder